import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { apiClient, RegisterRequest, cleanErrorMessage } from '../api/client';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ModeToggle } from '@/components/mode-toggle';
import { DynamicLogo } from '@/components/DynamicLogo';
import { PasswordStrengthIndicator } from '@/components/PasswordStrengthIndicator';

interface ExtendedRegisterRequest extends RegisterRequest {
  confirmPassword: string;
}

export function Register() {
  const navigate = useNavigate();
  const location = useLocation();
  const invitationCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isNavigatingRef = useRef(false);
  
  const [formData, setFormData] = useState<ExtendedRegisterRequest>({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<Partial<ExtendedRegisterRequest>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isInvited, setIsInvited] = useState(false);
  const [organizationName, setOrganizationName] = useState('');
  const [prefilledFromQuery, setPrefilledFromQuery] = useState(false);
  const [passwordIsValid, setPasswordIsValid] = useState(false);
  const [passwordFailedRequirements, setPasswordFailedRequirements] = useState<string[]>([]);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  // Cleanup timeouts on component unmount
  useEffect(() => {
    return () => {
      if (invitationCheckTimeoutRef.current) {
        clearTimeout(invitationCheckTimeoutRef.current);
      }
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  // Prefill email from query parameter if present (e.g., /register?email=jane@acme.com)
  useEffect(() => {
    const search = typeof window !== 'undefined' ? window.location.search : location.search;
    const params = new URLSearchParams(search || '');
    const emailParam = params.get('email');
    if (emailParam && typeof emailParam === 'string') {
      const cleaned = emailParam.trim();
      setFormData(prev => ({ ...prev, email: cleaned }));
      // Trigger invitation check immediately for prefilled email
      checkInvitationStatus(cleaned);
      setPrefilledFromQuery(true);
    }
  }, [location.search]);

  // Check if email matches any pending invitation when email changes
  const checkInvitationStatus = async (email: string) => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setIsInvited(false);
      setOrganizationName('');
      return;
    }

    try {
      // Check if this email has a pending invitation
      const invitations = await apiClient.listInvitations();
      const matchingInvitation = invitations.invitations?.find(
        (inv: any) => inv.email.toLowerCase() === email.toLowerCase() && inv.status === 'pending'
      );

      if (matchingInvitation) {
        setIsInvited(true);
        // Get organization name from tenant info
        try {
          const tenant = await apiClient.getTenant();
          setOrganizationName(tenant.name || '');
        } catch (tenantError) {
          setOrganizationName('');
        }
      } else {
        setIsInvited(false);
        setOrganizationName('');
      }
    } catch (error) {
      // If we can't check invitations, proceed with normal registration
      setIsInvited(false);
      setOrganizationName('');
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<ExtendedRegisterRequest> = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation is now handled by PasswordStrengthIndicator
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!passwordIsValid) {
      newErrors.password = passwordFailedRequirements[0] || 'Password does not meet requirements';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setApiError('');
    isNavigatingRef.current = true; // Mark that we're navigating

    try {
      // Register with email and password
      const registerData: RegisterRequest = {
        email: formData.email,
        password: formData.password,
      };
      
      const response = await apiClient.register(registerData);
      
      // Check if user was invited based on response message
      const wasInvited = response.message.includes('Welcome to the organization!');
      
      // Check if email verification is needed based on response message
      const needsEmailVerification = response.message.includes('check your email') || response.message.includes('verification code');
      
      if (needsEmailVerification) {
        // Email verification required (for both invited and non-invited users)
        if (wasInvited || isInvited) {
          setSuccessMessage('Registration successful! Redirecting to email verification to join your organization...');
        } else {
          setSuccessMessage('Account created! Redirecting to email verification...');
        }
        // Store navigation data before setTimeout to prevent closure issues
        const navigationState = {
          email: formData.email,
          userId: response.user_id,
          isInvited: wasInvited || isInvited
        };
        navigationTimeoutRef.current = setTimeout(() => {
          navigate('/verify-email', {
            state: navigationState
          });
        }, 1500);
      } else if (wasInvited || isInvited) {
        // Invited users without email verification (emails disabled) - go to login
        setSuccessMessage(`Registration successful! You can now log in to join ${organizationName || 'the organization'}.`);
        const navigationState = { email: formData.email };
        navigationTimeoutRef.current = setTimeout(() => {
          navigate('/login', {
            state: navigationState
          });
        }, 2000);
      } else {
        // Non-invited users without email verification (emails disabled) —
        // account and organization are already created (with a placeholder
        // name the setup wizard offers to rename after login).
        setSuccessMessage('Account created! Redirecting to login...');
        const navigationState = { email: formData.email };
        navigationTimeoutRef.current = setTimeout(() => {
          navigate('/login', {
            state: navigationState
          });
        }, 1500);
      }
    } catch (error: any) {
      const friendly = cleanErrorMessage(error);
      if (/already exists/i.test(friendly)) {
        setSuccessMessage('Account already exists. Redirecting you to sign in...');
        navigationTimeoutRef.current = setTimeout(() => {
          navigate('/login', { state: { email: formData.email } });
        }, 1200);
        return;
      }
      setApiError(friendly || 'Registration failed. Please try again.');
      isNavigatingRef.current = false; // Reset flag on error
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field when user starts typing
    if (errors[name as keyof ExtendedRegisterRequest]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Check invitation status when email changes
    if (name === 'email') {
      // Clear any existing timeout to debounce the invitation check
      if (invitationCheckTimeoutRef.current) {
        clearTimeout(invitationCheckTimeoutRef.current);
      }
      
      // Set a new timeout for the invitation check
      invitationCheckTimeoutRef.current = setTimeout(() => {
        checkInvitationStatus(value);
      }, 500);
    }
  };



  const handleGoogleRegister = async () => {
    setIsGoogleLoading(true);
    setApiError('');

    try {
      const response = await fetch('/api/auth/google/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to initiate Google sign-up');
      }

      const result = await response.json();

      if (!result.google_oauth_available) {
        setApiError('Google sign-up is not currently available. Please register with email and password.');
        setIsGoogleLoading(false);
        return;
      }

      window.location.href = result.auth_url;
    } catch (err: any) {
      console.error('[Google OAuth Register] Error:', err);
      setApiError(cleanErrorMessage(err) || 'Failed to initiate Google sign-up');
      setIsGoogleLoading(false);
    }
  };

  const handleGitHubRegister = async () => {
    setIsGoogleLoading(true);
    setApiError('');

    try {
      const response = await fetch('/api/auth/github/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to initiate GitHub sign-up');
      }

      const result = await response.json();

      if (!result.github_oauth_available) {
        setApiError('GitHub sign-up is not currently available. Please register with email and password.');
        setIsGoogleLoading(false);
        return;
      }

      window.location.href = result.auth_url;
    } catch (err: any) {
      console.error('[GitHub OAuth Register] Error:', err);
      setApiError(cleanErrorMessage(err) || 'Failed to initiate GitHub sign-up');
      setIsGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-100 dark:bg-background dark:from-transparent dark:via-transparent dark:to-transparent flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      {/* Logo in top left */}
      <div className="absolute top-4 left-6">
        <DynamicLogo size="lg" className="drop-shadow-lg" />
      </div>
      
      {/* Theme toggle */}
      <div className="absolute top-6 right-6">
        <ModeToggle />
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-semibold text-foreground">
          {isInvited ? `Join ${organizationName}` : 'Welcome to Kestrel AI'}
        </h2>
        <p className="mt-2 text-center text-sm text-muted-foreground">
          {isInvited ? (
            `Complete your registration to access ${organizationName}`
          ) : (
            <>
              Or{' '}
              <Link to="/login" className="font-medium text-primary hover:text-primary/80">
                sign in to your existing account
              </Link>
            </>
          )}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card>
          <CardHeader>
            <CardTitle>{isInvited ? 'Complete your registration' : 'Create your account'}</CardTitle>
            <CardDescription>
              {isInvited 
                ? `You've been invited to join ${organizationName}. Set up your account to get started.`
                : 'Sign up with your email to get started'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {successMessage && (
              <Alert className="mb-6">
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  {successMessage}
                </AlertDescription>
              </Alert>
            )}

            {apiError && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{apiError}</AlertDescription>
              </Alert>
            )}

            {!isInvited && (
              <div className="mb-6">
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={handleGoogleRegister}
                    disabled={isLoading || isGoogleLoading}
                  >
                    {isGoogleLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                        Redirecting...
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-5 h-5 mr-2"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            fill="#4285F4"
                          />
                          <path
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            fill="#34A853"
                          />
                          <path
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            fill="#FBBC05"
                          />
                          <path
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            fill="#EA4335"
                          />
                        </svg>
                        Google
                      </>
                    )}
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={handleGitHubRegister}
                    disabled={isLoading || isGoogleLoading}
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"
                        fill="currentColor"
                      />
                    </svg>
                    GitHub
                  </Button>
                </div>

                <div className="relative mt-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-background text-muted-foreground">Or continue with</span>
                  </div>
                </div>
              </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@company.com"
                  className={errors.email ? "border-destructive" : ""}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email}</p>
                )}
                {isInvited && (
                  <p className="text-sm text-green-600 dark:text-green-400">
                    ✓ This email has been invited to join {organizationName}
                  </p>
                )}
                {prefilledFromQuery && !isInvited && (
                  <p className="text-xs text-muted-foreground">Continue with invited email</p>
                )}
                {!isInvited && !prefilledFromQuery && (
                  <p className="text-xs text-muted-foreground">
                    Work emails get $1,000 in usage credit vs $50 for personal emails
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className={errors.password ? "border-destructive" : ""}
                />
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password}</p>
                )}
                {/* Password Strength Indicator */}
                {formData.password && (
                  <PasswordStrengthIndicator
                    password={formData.password}
                    email={formData.email}
                    onValidationChange={(isValid, failures) => {
                      // Don't update state if we're in the process of navigating
                      if (!isNavigatingRef.current) {
                        setPasswordIsValid(isValid);
                        setPasswordFailedRequirements(failures);
                      }
                    }}
                    showRequirements={true}
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className={errors.confirmPassword ? "border-destructive" : ""}
                />
                {errors.confirmPassword && (
                  <p className="text-sm text-destructive">{errors.confirmPassword}</p>
                )}
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoading || (formData.password.length > 0 && !passwordIsValid)}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                    Creating account...
                  </>
                ) : (
                  isInvited ? 'Join Organization' : 'Create account'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 